import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, CheckIcon } from '@calendly/ui/components/icons';
import { Item } from '@calendly/ui/components/menu';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { primaryColor } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { useGetUserQuery } from 'common';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { useETEditorFormikPoolingType } from 'services/event_types';
import { FullStoryExclude, FullStoryMask } from '../../../../shared/fullstory_privacy';
import { getMemberName } from '../../utils';
import { useOrganizer } from '../hooks';
const containerCss = "c19gru7i";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const dropdownContainerCss = "d106imbu";
const DropdownContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dropdownContainerCss, props.className)} />;
});
const buttonLabelDisabledCss = "b1maarmb";
const buttonLabelCss = "b1aiuyka";
const ButtonLabel = forwardRef((props, ref) => {
  const {
    disabled,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonLabelCss, {
    [buttonLabelDisabledCss]: disabled
  }, props.className)} />;
});
const optionContainerCss = "o1xwy7r4";
const OptionContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionContainerCss, props.className)} />;
});
const optionWrapperCss = "o2i8mf8";
const OptionWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionWrapperCss, props.className)} />;
});
const infoWrapperCss = "iixxyx";
const InfoWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(infoWrapperCss, props.className)} />;
});
const emailWrapperCss = "e9qojao";
const EmailWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emailWrapperCss, props.className)} />;
});
const OrganizerOption = ({
  option,
  variant = 'small',
  currentUser,
  selected = false
}) => <OptionContainer>
    <OptionWrapper>
      <FullStoryExclude>
        <Avatar size={variant} src={option.avatar_url} letter={option.avatar_letter} alt={option.name} />
      </FullStoryExclude>
      <InfoWrapper>
        <BodyText>
          <FullStoryMask>{getMemberName(option, currentUser)}</FullStoryMask>
        </BodyText>
        {variant !== 'x-small' && <EmailWrapper>
          <BodyText>
            <FullStoryMask>{option.email}</FullStoryMask>
          </BodyText>
        </EmailWrapper>}
      </InfoWrapper>

    </OptionWrapper>
    {selected && <CheckIcon color={primaryColor} />}
  </OptionContainer>;
const OrganizerTextButton = ({
  isOpen,
  onClick,
  children,
  loading,
  showChevron = true,
  disabled = false,
  ...rest
}) => <MinimalButton loading={loading} onClick={onClick} {...rest}>
    <ButtonLabel disabled={disabled}>
      <BodyText size="medium">{children}</BodyText>
      {showChevron && !isOpen && <ArrowChevronDownMiniIcon />}
      {showChevron && isOpen && <ArrowChevronUpMiniIcon />}
    </ButtonLabel>
  </MinimalButton>;
const OrganizerSelectDropdown = ({
  memberships,
  currentOrganizer,
  validLocationKind,
  onSelect
}) => {
  const {
    data: currentUser
  } = useGetUserQuery();
  const isPhone = useDeviceType('isPhone');
  const [toggle, Dropdown, isOpen, {
    dropdownId
  }] = useDropdown(false, true);
  const linkDisabled = ['google_conference', 'microsoft_teams_conference'].includes(validLocationKind);
  if (linkDisabled) {
    return <BodyText>
        <FullStoryMask>
          {getMemberName(currentOrganizer, currentUser)}
        </FullStoryMask>
      </BodyText>;
  }
  return <DropdownContainer>
      <OrganizerTextButton aria-label={I18n.t('publisher.event_type_editor.sections.event_details.location.select_organizer_label')} aria-expanded={isOpen} aria-controls={dropdownId} isOpen={isOpen} onClick={toggle}>
        <FullStoryMask>
          {getMemberName(currentOrganizer, currentUser)}
        </FullStoryMask>
      </OrganizerTextButton>
      <Dropdown>
        <ViewportDropdownMenu data-component="organizer-option-list" pullRight={!isPhone}>
          {memberships.map(option => <Item key={option.email} onClick={() => onSelect(option)}>
              <OrganizerOption option={option} currentUser={currentUser} selected={currentOrganizer.user_id === option.user_id} />
            </Item>)}
        </ViewportDropdownMenu>
      </Dropdown>
    </DropdownContainer>;
};

// i18n-tasks-use t('publisher.event_type_editor.sections.event_details.location.inlines.google_conference.header')
// i18n-tasks-use t('publisher.event_type_editor.sections.event_details.location.inlines.gotomeeting_conference.header')
// i18n-tasks-use t('publisher.event_type_editor.sections.event_details.location.inlines.microsoft_teams_conference.header')
// i18n-tasks-use t('publisher.event_type_editor.sections.event_details.location.inlines.webex_conference.header')
// i18n-tasks-use t('publisher.event_type_editor.sections.event_details.location.inlines.zoom_conference.header')

export const OrganizerSelect = ({
  kind,
  eventType
}) => {
  const {
    values: {
      locationConfigurations = [],
      userPools
    }
  } = useFormContext();
  const {
    isCollective,
    isCollectiveRoundRobin
  } = useETEditorFormikPoolingType();
  const isCRRWithCollectiveUserPools = isCollectiveRoundRobin && userPools?.some(pool => pool.pooling_type === 'collective');
  const hasCollectiveUserPools = isCollective || isCRRWithCollectiveUserPools;
  const {
    organizableConferenceLocation,
    organizerMemberships,
    currentOrganizer,
    updateOrganizer,
    updateLocationConfigurations
  } = useOrganizer(eventType, kind, locationConfigurations);
  const showOrganizerDropdown = organizerMemberships.length && organizableConferenceLocation && hasCollectiveUserPools;
  const onSelect = useCallback(membership => {
    updateOrganizer(membership);
    updateLocationConfigurations(membership);
  }, [updateLocationConfigurations, updateOrganizer]);
  if (!showOrganizerDropdown) return null;
  return <Container>
      <BodyText>
        {I18n.t(`publisher.event_type_editor.sections.event_details.location.inlines.${kind}.header`)}
        :
      </BodyText>
      <OrganizerSelectDropdown memberships={organizerMemberships} currentOrganizer={currentOrganizer} validLocationKind={kind} onSelect={onSelect} />
    </Container>;
};

require("./organizer_select.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./organizer_select.js");